import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatDateLocale } from '../utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRightLeft, Clock, User, Users, AlertCircle, History } from 'lucide-react';
import api from '../utils/api';
import { useRBAC } from '../context/RBACContext';
import { useTranslation } from 'react-i18next';

/**
 * AssignmentPanel — Shows current owner + assignment history for any entity.
 * Props:
 *   entityType: "lead" | "thread" | "patient"
 *   entityId: string
 *   clinicId: string (for fetching clinic members)
 *   userRole: string (caller's role) — B-3: DEPRECATED / non-authoritative.
 *     Preserved for callsite compatibility but never used to gate the
 *     Reassign button — role is now derived from `useRBAC()` which
 *     reads the canonical `treatx_rbac_role_assignments` claim.
 *   onAssignmentChange: () => void (callback after assignment)
 */
export function AssignmentPanel({ entityType, entityId, clinicId, userRole: _legacyUserRole, refreshKey = 0, onAssignmentChange }) {
  const { t } = useTranslation();
  // UAT-MSG-IDENTITY-DISPLAY-NAME — a raw internal id is never a display fallback.
  const staffLabel = (name) => name || t('provider.messaging.unresolvedStaffFallback');
  const [history, setHistory] = useState([]);
  const [currentOwner, setCurrentOwner] = useState(null);
  const [currentOwnerName, setCurrentOwnerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [error, setError] = useState('');

  // B-3 — canonical role source. The prior `localStorage.getItem
  // ('treatx_role')` returned NULL for every clinic_admin session
  // (the key never exists), so the Reassign button never rendered
  // and the whole /api/assignments/* UI path was dead. Derive from
  // the RBAC context which reads the JWT + role_assignments SSOT.
  const rbac = useRBAC();
  const canAssign = useMemo(() => {
    // Prefer permission-based gating (canonical RBAC contract) —
    // `assignment.manage` and `lead.assign` are the semantically
    // correct guards. Fall back to a role-id whitelist for older
    // sessions where permissions haven't been materialised.
    if (typeof rbac?.can === 'function') {
      if (rbac.can('assignment.manage') || rbac.can('lead.assign')) return true;
    }
    const roles = new Set();
    for (const ra of (rbac?.roleAssignments || [])) {
      if (ra?.role_id) roles.add(ra.role_id);
    }
    const allowed = new Set([
      'clinic_admin', 'provider_admin', 'provider_coordinator',
      'coordinator', 'treatx_admin', 'treatx_super_admin',
      'platform_admin',
    ]);
    for (const r of roles) if (allowed.has(r)) return true;
    return false;
  }, [rbac]);

  const fetchHistory = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      // NB: shared `api` axios instance already sets baseURL to
      // `${REACT_APP_BACKEND_URL}/api`, so paths here MUST NOT be
      // prefixed with `/api/` — otherwise the request lands on
      // `/api/api/...` and returns 404, silently leaving the
      // Assignment card in an "Unassigned" state while the underlying
      // canonical write succeeded (this WAS the production bug).
      const res = await api.get(`/assignments/history/${entityType}/${entityId}`);
      setHistory(res.data.history || []);
      setCurrentOwner(res.data.current_owner);
      setCurrentOwnerName(res.data.current_owner_name || '');
    } catch (err) {
      console.error('Failed to fetch assignment history:', err);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  const fetchTeam = useCallback(async () => {
    if (!clinicId) return;
    // B-3 — canonical team roster comes from `/api/clinic/team`
    // (36 members in the observed clinic). The prior
    // `/clinic/agents` surface returned 0 rows for c60's clinic and
    // the `/org/users` fallback 404s, so the reassign dropdown was
    // permanently empty. Normalise the shape and exclude the
    // current owner from the picker.
    const normalise = (raw) => {
      if (!raw) return [];
      const list = raw.members || raw.agents || raw.users || raw || [];
      const arr = Array.isArray(list) ? list : [];
      return arr
        .filter((m) => m && (m.user_id || m.id))
        .filter((m) => (m.user_id || m.id) !== currentOwner)
        .map((m) => ({
          user_id: m.user_id || m.id,
          name: m.name || m.display_name || m.email || m.user_id || m.id,
          email: m.email || '',
          role: m.role || m.role_id || '',
        }));
    };

    try {
      const res = await api.get(`/clinic/team`);
      let team = normalise(res.data);
      if (team.length === 0) {
        // Fall back only when /clinic/team is EMPTY, not only on throw.
        try {
          const res2 = await api.get(`/clinic/agents?clinic_id=${clinicId}`);
          team = normalise(res2.data);
        } catch { /* keep empty */ }
      }
      setTeamMembers(team);
    } catch (err) {
      try {
        const res = await api.get(`/clinic/agents?clinic_id=${clinicId}`);
        setTeamMembers(normalise(res.data));
      } catch {
        setTeamMembers([]);
      }
    }
  }, [clinicId, currentOwner]);

  useEffect(() => { fetchHistory(); }, [fetchHistory, refreshKey]);
  useEffect(() => { if (showReassign) fetchTeam(); }, [showReassign, fetchTeam]);

  const handleAssign = async () => {
    if (!selectedMember) return;
    setReassigning(true);
    setError('');
    try {
      const endpoint = currentOwner ? '/assignments/reassign' : '/assignments/assign';
      const res = await api.post(endpoint, {
        entity_type: entityType,
        entity_id: entityId,
        assigned_to: selectedMember,
        reason: 'Reassigned via dashboard',
      });
      if (res.data.success) {
        setShowReassign(false);
        setSelectedMember('');
        await fetchHistory();
        if (onAssignmentChange) onAssignmentChange();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Assignment failed');
    } finally {
      setReassigning(false);
    }
  };

  return (
    <Card data-testid="assignment-panel" className="border border-gray-200">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <User className="w-4 h-4 text-blue-600" />
            Assignment
          </CardTitle>
          {canAssign && (
            <Button
              data-testid="reassign-btn"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setShowReassign(!showReassign)}
            >
              <ArrowRightLeft className="w-3 h-3 mr-1" />
              {currentOwner ? 'Reassign' : 'Assign'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-3">
        {/* Current Owner */}
        <div data-testid="current-owner-display" className="flex items-center gap-2">
          {currentOwner ? (
            <>
              <Badge variant="default" className="bg-green-100 text-green-800 text-xs" data-testid="current-owner-name">
                <Users className="w-3 h-3 mr-1" />
                {currentOwnerName || t('provider.messaging.assignedRepresentativeFallback')}
              </Badge>
            </>
          ) : (
            <Badge variant="destructive" className="text-xs">
              <AlertCircle className="w-3 h-3 mr-1" />
              Unassigned
            </Badge>
          )}
        </div>

        {/* Reassign UI */}
        {showReassign && (
          <div data-testid="reassign-form" className="border rounded-md p-3 bg-gray-50 space-y-2">
            <p className="text-xs text-gray-600 font-medium">Select new owner:</p>
            <Select value={selectedMember} onValueChange={setSelectedMember}>
              <SelectTrigger data-testid="member-select" className="h-8 text-xs">
                <SelectValue placeholder="Choose team member..." />
              </SelectTrigger>
              <SelectContent>
                {teamMembers.map((m) => (
                  <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>
                    {m.name || m.email || t('provider.messaging.assignedRepresentativeFallback')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex gap-2">
              <Button
                data-testid="confirm-assign-btn"
                size="sm"
                className="h-7 text-xs"
                onClick={handleAssign}
                disabled={!selectedMember || reassigning}
              >
                {reassigning ? 'Assigning...' : 'Confirm'}
              </Button>
              <Button
                data-testid="cancel-assign-btn"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => { setShowReassign(false); setError(''); }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* History Toggle */}
        <Button
          data-testid="toggle-history-btn"
          variant="ghost"
          size="sm"
          className="h-6 text-xs text-gray-500 p-0"
          onClick={() => setShowHistory(!showHistory)}
        >
          <History className="w-3 h-3 mr-1" />
          {showHistory ? 'Hide' : 'Show'} History ({history.length})
        </Button>

        {/* History List */}
        {showHistory && history.length > 0 && (
          <div data-testid="assignment-history" className="space-y-2 max-h-48 overflow-y-auto">
            {history.map((entry) => (
              <div key={entry.id} className="text-xs border-l-2 border-blue-200 pl-2 py-1" data-testid={`assignment-history-entry-${entry.id}`}>
                <div className="flex items-center gap-1 text-gray-700">
                  <Clock className="w-3 h-3 text-gray-400" />
                  <span className="font-medium">{entry.action === 'reassign' ? t('provider.messaging.historyReassigned') : t('provider.messaging.historyAssigned')}</span>
                </div>
                <p className="text-gray-500 ml-4">
                  {entry.assigned_from ? `${staffLabel(entry.assigned_from_name)} → ` : ''}
                  <span className="font-medium text-gray-700">{staffLabel(entry.assigned_to_name)}</span>
                </p>
                <p className="text-gray-400 ml-4">
                  {t('provider.messaging.historyBy', { name: staffLabel(entry.assigned_by_name) })} · {formatDateLocale(entry.created_at)}
                  {entry.reason && ` · "${entry.reason}"`}
                </p>
              </div>
            ))}
          </div>
        )}

        {showHistory && history.length === 0 && (
          <p className="text-xs text-gray-400 italic">No assignment history yet</p>
        )}
      </CardContent>
    </Card>
  );
}

export default AssignmentPanel;
